/* PLTX2 — Before / After slider.

   Infinite carousel on our own engine. Pltx2BfaFlick below is a duplicate of
   the Pltx2Flick engine that drives the video carousel — copied rather than
   shared, per the duplicate-and-modify rule, so tuning one slider can never
   move the other.

   Physics per frame (classic attraction/friction spring integrator):
     force = (target − x) · ATTRACTION
     v = (v + force) · (1 − FRICTION)
     x += v
   While dragging, x tracks the pointer 1:1 and v carries the last frame's
   delta, so a release flicks with momentum. Projected resting position
   x + v/FRICTION picks the landing card; a fast last-instant flick boosts one
   card in the flick direction. Cells wrap, so the strip is endless in both
   directions like the original testimonials field.

   Also here:
   - Dot pagination as a fixed 5-slot window. The row holds ALL dots and
     slides 16px per step behind it; a windowed re-render freezes visually
     through the middle of a long list, which reads as a stuck control.
   - Shimmer teardown per photo, so a slow connection sees a sweep rather
     than a dead rectangle. */
(() => {
  const ATTRACTION = 0.025;
  const FRICTION = 0.28;
  const STATIC_CLICK_MOVE = 3; // px — under this, a tap is a click
  const FLICK_WINDOW = 100; // ms — last-move recency for the boost

  const WINDOW = 5;
  const DOT = 8;
  const GAP = 8;
  const PILL = 24; // active dot stretches to a pill — must match --pltx2-bfa-pill
  const STEP = DOT + GAP;

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  class Pltx2BfaFlick {
    constructor(wrapper, options) {
      this.wrapper = wrapper;
      this.rtl = !!options.rightToLeft;
      this.onChange = options.onChange || (() => {});
      this.cells = [...wrapper.children];
      this.selectedIndex = -1; // so the initial select(0) applies classes
      this.x = 0;
      this.v = 0;
      this.target = 0;
      this.isDragging = false;
      this.#build();
      // class first: pre-init CSS lets the cells flow, and the engine only
      // owns layout once it can measure them
      wrapper.classList.add('pltx2-bfa-on');
      this.measure();
      this.select(0, true);
      this.#bind();
      // paint synchronously so there is no unstyled first frame (and a
      // throttled background tab still gets a correct static layout)
      this.#render();
      // measure()/select() already woke the loop — this is just a safety net
      this.#wake();
    }

    #raf = null;
    #pointerId = null;
    #pointerStartX = 0;
    #dragStartX = 0;
    #pointerX = 0;
    #maxMove = 0;
    #lastMoveDelta = 0;
    #lastMoveTime = 0;
    #idle = false;
    #unbind = null;
    #running = false;
    #cellShift = [];
    #sliderTx = null;

    #build() {
      const doc = this.wrapper.ownerDocument;
      this.viewport = doc.createElement('div');
      this.viewport.className = 'pltx2-bfa-viewport';
      this.slider = doc.createElement('div');
      this.slider.className = 'pltx2-bfa-slider';
      for (const cell of this.cells) {
        cell.style.position = 'absolute';
        cell.style.top = '0';
        cell.style[this.rtl ? 'right' : 'left'] = '0';
        this.slider.append(cell);
      }
      this.viewport.append(this.slider);
      this.wrapper.append(this.viewport);
      this.wrapper.tabIndex = 0;
    }

    /* cell geometry: positions include margins (outer widths), centers drive
       both the wrap shift and the per-cell attractor */
    measure() {
      // release the previous height lock first: the cards fill the viewport
      // (min-block-size: 100%), so measuring under the old lock would read
      // the lock back and maxH could grow but never shrink
      this.viewport.style.height = '';
      this.viewportW = this.viewport.offsetWidth || this.wrapper.offsetWidth;
      this.positions = [];
      this.centers = [];
      let cursor = 0;
      let maxH = 0;
      for (const cell of this.cells) {
        const cs = getComputedStyle(cell);
        const ml = parseFloat(cs.marginLeft) || 0;
        const mr = parseFloat(cs.marginRight) || 0;
        // rect, not offset*: keeps fractional sizes exact
        const rect = cell.getBoundingClientRect();
        this.positions.push(cursor);
        this.centers.push(cursor + ml + rect.width / 2);
        cursor += ml + rect.width + mr;
        maxH = Math.max(maxH, rect.height);
      }
      this.total = cursor;
      // tallest card sets the rail height, so a long quote cannot make the
      // section jump as you swipe
      this.viewport.style.height = maxH + 'px';
      // geometry moved, so every cached transform is stale
      this.#cellShift.length = 0;
      this.#sliderTx = null;
      this.#wake();
    }

    /* attractor for cell i: the x that centers it, in the wrap representation
       nearest to `near` */
    #attractor(index, near) {
      const base = this.centers[index] - this.viewportW / 2;
      return base + this.total * Math.round((near - base) / this.total);
    }

    #setSelected(index) {
      const n = this.cells.length;
      index = ((index % n) + n) % n;
      if (index === this.selectedIndex) return false;
      this.selectedIndex = index;
      for (let i = 0; i < n; i++) {
        this.cells[i].classList.toggle('is-selected', i === index);
      }
      this.onChange(index);
      return true;
    }

    select(index, instant) {
      this.#setSelected(index);
      this.target = this.#attractor(this.selectedIndex, this.x);
      if (instant) {
        this.x = this.target;
        this.v = 0;
      }
      this.#wake();
    }

    resize() {
      /* Never re-measure mid-gesture. select(..., true) snaps x instantly,
         so a resize landing during a drag teleports the strip out from under
         the finger. This fires per photo decode (20 of them, lazy), so on a
         slow connection it was very reachable — the "weird teleport". */
      if (this.isDragging) return;

      const prevW = this.viewportW;
      const prevTotal = this.total;
      this.measure();
      // widths unchanged (a photo decoded, only heights settled): the height
      // is already re-locked by measure(), and x needs no snapping
      if (this.viewportW === prevW && this.total === prevTotal) return;

      this.select(this.selectedIndex, true);
      this.#render();
    }

    destroy() {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = null;
      this.#running = false;
      this.#unbind?.();
    }

    #bind() {
      const vp = this.viewport;
      const onDown = (event) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.#pointerId = event.pointerId;
        this.#pointerStartX = this.#pointerX = event.clientX;
        this.#dragStartX = this.x;
        this.#maxMove = 0;
        this.#lastMoveDelta = 0;
        this.#lastMoveTime = 0;
        this.isDragging = true;
        this.#wake(); // the loop parks itself once settled — restart it
        vp.classList.add('is-pointer-down');
        try { vp.setPointerCapture(event.pointerId); } catch (e) { /* pointer already gone */ }
      };
      const onMove = (event) => {
        if (event.pointerId !== this.#pointerId) return;
        const delta = event.clientX - this.#pointerX;
        this.#pointerX = event.clientX;
        this.#maxMove = Math.max(this.#maxMove, Math.abs(event.clientX - this.#pointerStartX));
        this.#lastMoveDelta = this.rtl ? -delta : delta;
        this.#lastMoveTime = event.timeStamp;
        if (this.#maxMove > STATIC_CLICK_MOVE) event.preventDefault();
      };
      const onUp = (event) => {
        if (event.pointerId !== this.#pointerId) return;
        this.#pointerId = null;
        this.isDragging = false;
        vp.classList.remove('is-pointer-down');
        if (event.type === 'pointerup' && this.#maxMove < STATIC_CLICK_MOVE) {
          this.target = this.#attractor(this.selectedIndex, this.x);
          return;
        }
        this.#settleFromDrag(event.timeStamp);
      };
      const onKey = (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target !== this.wrapper) return;
        event.preventDefault();
        const forward = (event.key === 'ArrowRight') !== this.rtl;
        this.selectAdjacent(forward ? 1 : -1);
      };
      // the photos are native drag sources on desktop: without this, a mouse
      // swipe that starts on an image lifts a ghost image and cancels the
      // pointer stream mid-gesture — reads as "the slider broke"
      const onDragStart = (event) => event.preventDefault();
      vp.addEventListener('dragstart', onDragStart);
      vp.addEventListener('pointerdown', onDown);
      vp.addEventListener('pointermove', onMove);
      vp.addEventListener('pointerup', onUp);
      vp.addEventListener('pointercancel', onUp);
      this.wrapper.addEventListener('keydown', onKey);
      this.#unbind = () => {
        vp.removeEventListener('dragstart', onDragStart);
        vp.removeEventListener('pointerdown', onDown);
        vp.removeEventListener('pointermove', onMove);
        vp.removeEventListener('pointerup', onUp);
        vp.removeEventListener('pointercancel', onUp);
        this.wrapper.removeEventListener('keydown', onKey);
      };
    }

    /* move exactly one cell in the given logical direction */
    selectAdjacent(direction) {
      const next = this.selectedIndex + direction;
      this.#setSelected(next);
      this.target = this.#attractor(this.selectedIndex, this.x + direction * 0.001);
      // make sure the pull goes the requested way even across the wrap seam
      if (direction > 0 && this.target < this.x) this.target += this.total;
      if (direction < 0 && this.target > this.x) this.target -= this.total;
      this.#wake();
    }

    /* release: project the resting position from current velocity, land on
       the nearest attractor; a fast trailing flick boosts one cell over */
    #settleFromDrag(releaseTime) {
      const restingX = this.x + this.v / FRICTION;
      let bestIndex = 0;
      let bestTarget = 0;
      let bestDist = Infinity;
      for (let i = 0; i < this.cells.length; i++) {
        const candidate = this.#attractor(i, restingX);
        const dist = Math.abs(candidate - restingX);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = i;
          bestTarget = candidate;
        }
      }
      const flicked =
        releaseTime - this.#lastMoveTime < FLICK_WINDOW && Math.abs(this.#lastMoveDelta) > 1;
      if (bestIndex === this.selectedIndex && this.#maxMove > STATIC_CLICK_MOVE && flicked) {
        // drag direction: pointer moving toward inline-start advances x
        this.selectAdjacent(this.#lastMoveDelta < 0 ? 1 : -1);
        return;
      }
      this.#setSelected(bestIndex);
      this.target = bestTarget;
      this.#wake();
    }

    /* restart the loop if it parked — every state change that can move the
       strip funnels through here. Nothing visual is toggled: building or
       tearing down compositor layers at gesture boundaries is what sank the
       previous optimisation attempt (MISSION-15 §3). */
    #wake() {
      this.#idle = false;
      if (this.#running) return;
      this.#running = true;
      this.#raf = requestAnimationFrame(this.#tick);
    }

    #tick = () => {
      if (this.isDragging) {
        const dxScreen = this.#pointerX - this.#pointerStartX;
        const newX = this.#dragStartX - (this.rtl ? -dxScreen : dxScreen);
        this.v = newX - this.x;
        this.x = newX;
        this.#render();
      } else if (!this.#idle) {
        const force = (this.target - this.x) * ATTRACTION;
        this.v = (this.v + force) * (1 - FRICTION);
        this.x += this.v;
        if (Math.abs(this.target - this.x) < 0.4 && Math.abs(this.v) < 0.4) {
          this.x = this.target;
          this.v = 0;
          this.#idle = true;
        }
        this.#render();
      } else {
        /* settled: park the loop. The old version re-armed forever, waking
           the main thread every ~16ms for the life of the page with nothing
           moving. Parking is purely internal — no styles change here. */
        this.#running = false;
        this.#raf = null;
        return;
      }
      this.#raf = requestAnimationFrame(this.#tick);
    };

    /* The slider carries the motion: ONE transform write per frame moves the
       whole strip on a single compositor layer (will-change lives in the
       stylesheet). Cells re-position only when their wrap representation
       changes — their nearest-to-center copy jumps a full strip length —
       which is rare and happens at least a viewport away from being seen.
       The old per-cell version wrote 10 transforms (and allocated 10 toFixed
       strings) every frame. */
    #render() {
      // 1/64px keeps sub-pixel smoothness without toFixed's per-frame string
      const sx = Math.round((this.rtl ? this.x : -this.x) * 64) / 64;
      if (sx !== this.#sliderTx) {
        this.#sliderTx = sx;
        this.slider.style.transform = 'translate3d(' + sx + 'px,0,0)';
      }
      for (let i = 0; i < this.cells.length; i++) {
        const rel = this.centers[i] - this.x;
        const shift = this.total * Math.round((this.viewportW / 2 - rel) / this.total);
        if (shift === this.#cellShift[i]) continue;
        this.#cellShift[i] = shift;
        const pos = this.positions[i] + shift;
        this.cells[i].style.transform = 'translate3d(' + (this.rtl ? -pos : pos) + 'px,0,0)';
      }
    }
  }

  class Pltx2BeforeAfter {
    constructor(root) {
      this.root = root;
      this.wrapper = root.querySelector('[data-pltx2-bfa-wrapper]');
      this.dotsRow = root.querySelector('[data-pltx2-bfa-dots-row]');
      this.dotsVp = root.querySelector('.pltx2-bfa__dots');
      this.cards = [...root.querySelectorAll('.pltx2-bfa__card')];
      if (!this.wrapper || this.cards.length === 0) return;

      this.#watchImages();

      const rtl =
        (root.closest('[dir]')?.getAttribute('dir') ||
          document.documentElement.getAttribute('dir')) === 'rtl';

      this.#buildDots();
      this.engine = new Pltx2BfaFlick(this.wrapper, {
        rightToLeft: rtl,
        onChange: (index) => this.#paintDots(index)
      });
      this.#paintDots(0);

      // cards are image-driven, so their height only settles once photos
      // decode — re-measure then, and on resize
      this.#onResize = () => {
        clearTimeout(this.#resizeTimer);
        this.#resizeTimer = setTimeout(() => this.engine?.resize(), 180);
      };
      window.addEventListener('resize', this.#onResize);
    }

    #resizeTimer = null;
    #onResize = null;

    /* Drop the shimmer the moment a photo has pixels. Cached images report
       complete synchronously, so check before wiring a listener. */
    #watchImages() {
      for (const shot of this.root.querySelectorAll('.pltx2-bfa__shot')) {
        const img = shot.querySelector('.pltx2-bfa__img');
        // no image configured — nothing will ever load, so don't shimmer
        if (!img) {
          shot.classList.remove('is-loading');
          continue;
        }
        const clear = () => {
          shot.classList.remove('is-loading');
          this.#onResize?.();
        };
        if (img.complete && img.naturalWidth > 0) {
          shot.classList.remove('is-loading');
          continue;
        }
        img.addEventListener('load', clear, { once: true });
        // a broken source must not shimmer forever
        img.addEventListener('error', clear, { once: true });
      }
    }

    #buildDots() {
      if (!this.dotsRow || this.cards.length < 2) return;
      const doc = this.root.ownerDocument;
      this.dots = this.cards.map((_, i) => {
        const dot = doc.createElement('button');
        dot.type = 'button';
        dot.className = 'pltx2-bfa__dot';
        dot.tabIndex = -1; // the rail is the keyboard target, not N dots
        dot.setAttribute('aria-label', pltxT('pltx2.js.slide_to', 'מעבר לכרטיס') + ' ' + (i + 1));
        dot.addEventListener('click', () => this.engine?.select(i));
        this.dotsRow.append(dot);
        return dot;
      });
    }

    #paintDots(index) {
      if (!this.dots) return;
      for (let i = 0; i < this.dots.length; i++) {
        this.dots[i].classList.toggle('is-active', i === index);
      }
      const total = this.dots.length;

      /* Size the clipping viewport to what is actually on show. The CSS width
         assumes a full 5 slots; with 2-4 cards flex-start would strand the
         dots against one edge of an 88px box. Width = the idle slots plus the
         pill plus the gaps between them. */
      if (this.dotsVp) {
        const visible = Math.min(total, WINDOW);
        this.dotsVp.style.inlineSize =
          (visible - 1) * STEP + PILL + 'px';
      }

      if (total <= WINDOW) return;
      // keep the active pill inside the window: it walks freely near either
      // end and sits centred while the row glides through the middle
      const first = clamp(index - Math.floor(WINDOW / 2), 0, total - WINDOW);
      const rtl = getComputedStyle(this.root).direction === 'rtl';
      const shift = first * STEP;
      this.dotsRow.style.transform = `translateX(${rtl ? shift : -shift}px)`;
    }
  }

  const boot = () => {
    for (const root of document.querySelectorAll('[data-pltx2-bfa]')) {
      if (root.dataset.pltx2BfaBound) continue;
      root.dataset.pltx2BfaBound = 'true';
      new Pltx2BeforeAfter(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // theme editor: re-bind when a section is added or re-rendered
  document.addEventListener('shopify:section:load', boot);
})();
